import React, { useState } from 'react';
    import { motion, AnimatePresence } from 'framer-motion';
    import { useAuth } from '../context/AuthContext';
    import { useData } from '../context/DataContext';
    import UserForm from './UserForm';
    import TutorStudentsModal from './TutorStudentsModal';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiPlus, FiEdit, FiTrash2, FiUser, FiShield, FiLock, FiMail, FiPhone, FiEye, FiSearch, FiCheck } = FiIcons;

    const UserManagement = () => {
      const { users, appSettings } = useData();
      const { deleteUser } = useAuth();
      const [showAddForm, setShowAddForm] = useState(false);
      const [editingUser, setEditingUser] = useState(null);
      const [viewingTutor, setViewingTutor] = useState(null);
      const [searchTerm, setSearchTerm] = useState('');

      const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this user? This will also remove their Firebase Authentication account.')) {
          const result = await deleteUser(id);
          if (!result.success) {
            alert(result.message);
          }
        }
      };

      const getRoleColor = (role) => {
        const colors = {
          'admin': 'bg-rose-50 text-rose-600',
          'tutor': 'bg-indigo-50 text-indigo-600',
          'parent': 'bg-emerald-50 text-emerald-600',
          'viewer': 'bg-gray-100 text-gray-500'
        };
        return colors[role] || 'bg-gray-100 text-gray-800';
      };

      const filteredUsers = users.filter(user => {
        const search = searchTerm.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return (
          fullName.includes(search) ||
          user.email?.toLowerCase().includes(search) ||
          user.role?.toLowerCase().includes(search)
        );
      });

      return (
        <div className="space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h2 className="text-4xl font-black text-gray-900 tracking-tight">System Users</h2>
              <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest">Access Control & Staff Directory</p>
            </div>
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }} 
              onClick={() => setShowAddForm(true)} 
              className="flex items-center gap-3 px-8 py-4 bg-indigo-600 text-white rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-indigo-100 transition-all hover:bg-indigo-700"
              style={{ backgroundColor: appSettings.brandColor }}
            >
              <SafeIcon icon={FiPlus} className="text-lg" />
              Add System User
            </motion.button>
          </div>

          <div className="bg-white p-4 rounded-[2.5rem] shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input 
                type="text" 
                placeholder="Search staff, tutors or parents by name, email or role..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent rounded-[1.8rem] focus:bg-white focus:border-indigo-600 outline-none transition-all font-bold text-gray-700" 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode='popLayout'>
              {filteredUsers.map((user, index) => (
                <motion.div 
                  layout 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, scale: 0.9 }} 
                  key={user.id} 
                  className="bg-white rounded-[2.5rem] p-8 shadow-sm border-2 border-transparent hover:border-indigo-600/10 hover:shadow-2xl hover:shadow-indigo-100 transition-all group relative overflow-hidden"
                >
                  <div className="flex justify-between items-start mb-8">
                    <div className={`w-16 h-16 rounded-[1.5rem] flex items-center justify-center transition-colors ${
                      user.role === 'admin' ? 'bg-rose-50 text-rose-600' : 'bg-indigo-50 text-indigo-600'
                    }`}>
                      <SafeIcon icon={user.role === 'admin' ? FiShield : FiUser} className="text-3xl" />
                    </div>
                    <span className={`text-[10px] font-black px-4 py-1.5 rounded-full tracking-widest uppercase ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </div>

                  <h3 className="text-2xl font-black text-gray-900 mb-1 tracking-tight">{user.firstName} {user.lastName}</h3>
                  <p className="text-xs font-bold text-gray-400 mb-8 uppercase tracking-widest truncate">{user.email}</p>

                  <div className="bg-gray-50 rounded-[1.5rem] p-5 mb-8">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Account Status</span>
                      <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1">
                        <SafeIcon icon={FiCheck} /> {user.status || 'Active'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {user.permissions?.canManageStudents && <div className="w-2 h-2 bg-green-400 rounded-full" title="Students"></div>}
                      {user.permissions?.canManageUsers && <div className="w-2 h-2 bg-rose-400 rounded-full" title="Users"></div>}
                      {user.permissions?.canViewReports && <div className="w-2 h-2 bg-blue-400 rounded-full" title="Reports"></div>}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setEditingUser(user)} 
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-100"
                    >
                      Management
                    </button>
                    {user.role === 'tutor' && (
                      <button onClick={() => setViewingTutor(user)} className="p-3.5 bg-gray-50 text-gray-400 rounded-2xl hover:bg-indigo-50 hover:text-indigo-600 transition-all border border-transparent hover:border-indigo-100">
                        <SafeIcon icon={FiEye} />
                      </button>
                    )}
                    <button 
                      onClick={() => handleDelete(user.id)} 
                      disabled={user.role === 'admin' && users.filter(u => u.role === 'admin').length === 1}
                      className="p-3.5 bg-rose-50 text-rose-300 rounded-2xl hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {showAddForm && <UserForm onClose={() => setShowAddForm(false)} onSave={() => setShowAddForm(false)} />}
          {editingUser && <UserForm user={editingUser} onClose={() => setEditingUser(null)} onSave={() => setEditingUser(null)} />}
          {viewingTutor && <TutorStudentsModal tutor={viewingTutor} onClose={() => setViewingTutor(null)} />}
        </div>
      );
    };

    export default UserManagement;